// Getting Started articles for the Help Center.
// Content blocks: { type: 'p' | 'steps' | 'list' | 'note', text or items }

const gettingStarted = {
  slug: 'getting-started',
  label: 'Getting Started',
  icon: 'PiCompass',
  description: 'Create your account and learn your way around the site.',
  articles: [
    {
      slug: 'creating-your-account',
      title: 'Creating your account',
      summary: 'Sign up with your school email and you are ready to go.',
      body: [
        {
          type: 'p',
          text: 'You can sign up with an email address and password, or use one of the one click sign in options if they are available. There is no separate application process and no waiting period. Once you confirm your details you can start browsing services, messaging people, or setting up a listing right away.',
        },
        {
          type: 'steps',
          items: [
            'Go to the sign up page from the top of the site.',
            'Enter your name, email, and a password.',
            'Add your university when asked. This helps other students find you and helps you find services from people at your own school.',
            'Finish setting up your profile so people know who they are talking to.',
          ],
        },
        {
          type: 'note',
          text: 'You only need one account. There is no separate account type for buyers and sellers, so you never have to sign up twice.',
        },
        {
          type: 'note',
          text: 'Signed up with email and password? You will need to verify that address within a few days. Check your Junk or Spam folder first if you used a school (.edu) email, and see the Verifying your email article in Account & Settings if you need to resend the link.',
        },
      ],
    },
    {
      slug: 'how-buying-and-selling-work-together',
      title: 'Buying and selling on one account',
      summary: 'One login covers both hiring people and offering your own services.',
      body: [
        {
          type: 'p',
          text: 'Every account on this site can do both things: hire other students for work, and offer your own services to be hired. Nothing forces you to pick one path. A lot of people do both at the same time, for example hiring a designer for a project while also selling tutoring sessions of their own.',
        },
        {
          type: 'p',
          text: 'You will see this reflected around the site. The main navigation has separate sections for finding talent to hire and for finding work to do, and your profile can show both a history of things you have bought and things you have sold.',
        },
        {
          type: 'list',
          items: [
            'To hire someone, use Hire Talent to browse services or Post a Job to describe what you need.',
            'To offer your own services, use Post a Service to create a listing.',
            'Everything you buy or sell shows up in Orders and in your Inbox.',
          ],
        },
      ],
    },
    {
      slug: 'setting-up-your-profile',
      title: 'Setting up your profile',
      summary: 'A finished profile is what makes people trust you enough to hire or buy from you.',
      body: [
        {
          type: 'p',
          text: 'Your profile is what other students see before they message you, hire you, or buy from you. A profile with a photo, a short bio, and your university filled in gets far more responses than a blank one.',
        },
        {
          type: 'steps',
          items: [
            'Open your account menu and choose Settings.',
            'Add a profile photo. A real photo of you does better than a logo or a blank avatar.',
            'Write a short bio that says who you are and what you are good at.',
            'Fill in your university and, if you offer services, your skills.',
          ],
        },
        {
          type: 'note',
          text: 'Freelancer profiles and business profiles are tracked separately in Settings, so if you sell services under your own name and also help run a club or small business page, you can fill both in.',
        },
      ],
    },
    {
      slug: 'importing-your-profile-from-a-resume',
      title: 'Importing your profile from a résumé',
      summary: 'Upload a PDF and get a headline, bio, skills, and work history drafted for you to review.',
      body: [
        {
          type: 'p',
          text: 'The first time you set up a seller profile, you can upload a résumé instead of filling in every field by hand. Upload a PDF and it drafts your headline, bio, skills, and work history from it, usually in under a minute.',
        },
        {
          type: 'p',
          text: 'You get a chance to review everything it pulled out before moving on, and every field stays fully editable, both right there and later from your profile, if something needs fixing.',
        },
        {
          type: 'note',
          text: 'If you would rather not use a résumé, skip it. Filling the profile in yourself works exactly the same either way.',
        },
      ],
    },
    {
      slug: 'adding-your-university',
      title: 'Adding your university',
      summary: 'Your school shows up on your profile, your listings, and in search filters.',
      body: [
        {
          type: 'p',
          text: 'This site is built around university communities, so your school is one of the first things people see about you. It shows up on your profile, on any service you list, and it is one of the filters buyers can use when searching for talent.',
        },
        {
          type: 'p',
          text: 'If you attend more than one school, or you have graduated but want to keep an older affiliation on file, you can list more than one university. The one you set as primary is the one shown by default on your profile and listings.',
        },
        {
          type: 'steps',
          items: [
            'Go to Settings and open Personal Info or Freelancer Profile.',
            'Add your university from the list.',
            'If you have more than one, mark which one should show first.',
          ],
        },
      ],
    },
    {
      slug: 'finding-your-way-around',
      title: 'Finding your way around the site',
      summary: 'A quick map of the main sections and where everything lives.',
      body: [
        {
          type: 'p',
          text: 'The site is organized around a few main areas. Once you know where each one is, you can find your way to anything else from there.',
        },
        {
          type: 'list',
          items: [
            'Hire Talent: browse services other students are offering, grouped by category.',
            'Find Work: for freelancers, this is where you post a new service listing.',
            'Post a Job: for people who want to describe a project and let freelancers apply, instead of buying a fixed package.',
            'Academy: courses, live seminars, tutorials, and podcasts from other students and instructors.',
            'Inbox: every conversation, order, offer, and service agreement you are part of.',
            'Orders: a list of everything you have bought or sold, with its current status.',
            'Settings: your profile, payment methods, payout setup, and account preferences.',
          ],
        },
        {
          type: 'note',
          text: 'Most actions that involve another person, like agreeing on a service or paying for one, happen inside a conversation in your Inbox rather than on a separate checkout page.',
        },
      ],
    },
  ],
};

export default gettingStarted;
